import React, { Dispatch, SetStateAction } from "react";
import { toKey } from "../../components/editModalFunctions";
import ProductTypeCreateInline from "../../components/ProductTypeCreateInline";
import { parseLooseDateToISO } from "../../lib/dateUtils";
import { resolveProductTypeSelection } from "../../lib/productTypeSelection";
type FormState = any;

type AiSuggestions = {
  manufacturer?: string | null;
  model?: string | null;
  productType?: string | null;
};

type Props = {
  StepIndicator: React.ComponentType;
  currentStep: number | string;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  nextStep: () => void;
  prevStep: () => void;
  stepValid: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  customerListOpts: Array<{ key: string; name: string }>;
  showNewCustomerInput: boolean;
  setShowNewCustomerInput: (v: boolean) => void;
  newCustomer: string;
  setNewCustomer: (v: string) => void;
  handleNewCustomerAdd: () => void;
  manufacturerList: string[];
  aiSuggestions: AiSuggestions;
  userEditedManufacturerRef: React.MutableRefObject<boolean>;
  userEditedModelRef: React.MutableRefObject<boolean>;
  setShowNewManufacturerInput: (v: boolean) => void;
  showNewManufacturerInput: boolean;
  setNewManufacturer: (v: string) => void;
  newManufacturer: string;
  manufacturerExists: boolean;
  handleNewManufacturerAdd: () => void;
  modelList: string[];
  showNewModelInput: boolean;
  setShowNewModelInput: (v: boolean) => void;
  newModel: string;
  setNewModel: (v: string) => void;
  modelExistsForThisManufacturer: boolean;
  handleNewModelAdd: () => void;
  rpTypeOpts: any[];
  setShowNewProductTypeInput: (v: boolean) => void;
  showNewProductTypeInput: boolean;
  newProductType: string;
  setNewProductType: (v: string) => void;
  productTypeExists: boolean;
  ensureProductTypeInDb: (name: string) => Promise<any>;
  setRpTypeOpts: (opts: any[]) => void;
  setRpTypesPrimed: (v: boolean) => void;
  normalizeSerialKey: (s: string) => string;
  splitSerialParts: (s: string) => { base: string; visit: number };
  handleIsoDateInput: (e: React.FormEvent<HTMLInputElement>) => void;
  markNoWarranty: (focusAfter?: boolean) => void;
  openWarranty: (args: { manufacturer: string; serialBase: string; form: FormState; focusAfter?: boolean }) => void | Promise<void>;
  thumbnailPreviews: Record<string, string | null>;
  handlePhotoChange: (e: React.ChangeEvent<HTMLInputElement>, key: any) => void | Promise<void>;
  handleRemovePhoto: (key: any) => void;
  handleSave: () => void | Promise<void>;
  isSaving: boolean;
  showNewArticleInput: boolean;
  setShowNewArticleInput: (v: boolean) => void;
  newArticle: string;
  setNewArticle: (v: string) => void;
  handleNewArticleAdd: () => void;
  articleList: string[];
  overallProgress: number;
  formatSerialForDisplay?: (s: any) => string;
  fmtDateOnly?: (d: any) => string;
};

// 1:1-kopia av wizard-blocket från App.tsx (steps 1–11), utan ytterligare wrapper.
export function HomeWizardView(props: Props): JSX.Element {
  const {
    StepIndicator,
    currentStep,
    form,
    setForm,
    nextStep,
    prevStep,
    stepValid,
    handleInputChange,
    customerListOpts,
    showNewCustomerInput,
    setShowNewCustomerInput,
    newCustomer,
    setNewCustomer,
    handleNewCustomerAdd,
    manufacturerList,
    aiSuggestions,
    userEditedManufacturerRef,
    userEditedModelRef,
    setShowNewManufacturerInput,
    showNewManufacturerInput,
    setNewManufacturer,
    newManufacturer,
    manufacturerExists,
    handleNewManufacturerAdd,
    modelList,
    showNewModelInput,
    setShowNewModelInput,
    newModel,
    setNewModel,
    modelExistsForThisManufacturer,
    handleNewModelAdd,
    rpTypeOpts,
    setShowNewProductTypeInput,
    showNewProductTypeInput,
    newProductType,
    setNewProductType,
    productTypeExists,
    ensureProductTypeInDb,
    setRpTypeOpts,
    setRpTypesPrimed,
    normalizeSerialKey,
    splitSerialParts,
    handleIsoDateInput,
    markNoWarranty,
    openWarranty,
    thumbnailPreviews,
    handlePhotoChange,
    handleRemovePhoto,
    handleSave,
    isSaving,
    showNewArticleInput,
    setShowNewArticleInput,
    newArticle,
    setNewArticle,
    handleNewArticleAdd,
    articleList,
    overallProgress,
  } = props;

  type PhotoKey = any;

  const renderHeader = (title?: string, leftExtras?: React.ReactNode) => {
    const hasLeft = Boolean(title) || Boolean(leftExtras);
    return (
      <div
        className={`flex items-center gap-3 mb-3 ${
          hasLeft ? "justify-between" : "justify-center"
        }`}
      >
        {hasLeft && (
          <div className="flex items-center gap-2">
            {title && <h3 className="gw-h3 m-0">{title}</h3>}
            {leftExtras}
          </div>
        )}
        <StepIndicator />
      </div>
    );
  };

  return (
    <>
      {/* STEG 1 */}
      {Number(currentStep) === 1 && (
        <div className="gw-card">
          {renderHeader()}
          <div className="mb-4">
            <input
              name="orderNumber"
              type="text"
              placeholder="Ordernummer"
              value={form.orderNumber}
              onChange={handleInputChange}
              className="gw-input"
            />
          </div>
          <div className="gw-actions">
            <div />
            <button onClick={nextStep} className="gw-btn gw-btn-primary" disabled={!stepValid}>
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 2 */}
      {Number(currentStep) === 2 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={form.customerKey ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "new") {
                  setShowNewCustomerInput(true);
                  setForm((p: any) => ({ ...p, customer: "", customerKey: "" }));
                } else {
                  setShowNewCustomerInput(false);
                  const opt = Array.isArray(customerListOpts)
                    ? customerListOpts.find((o: any) => o.key === v)
                    : undefined;
                  setForm((p: any) => ({
                    ...p,
                    customer: opt?.name ?? "",
                    customerKey: opt?.key ?? "",
                  }));
                }
              }}
              className="gw-input w-full"
            >
              <option value="">Kund</option>
              {(Array.isArray(customerListOpts) ? customerListOpts : []).map((opt: any) => (
                <option key={opt.key} value={opt.key}>
                  {opt.name}
                </option>
              ))}
              <option value="new">Lägg till ny kund</option>
            </select>

            {showNewCustomerInput && (
              <>
                <input
                  type="text"
                  placeholder="Ny kund"
                  value={newCustomer}
                  onChange={(e) => setNewCustomer(e.target.value)}
                  className="gw-input"
                />
                <button type="button" onClick={handleNewCustomerAdd} className="gw-btn gw-btn-secondary" disabled={!newCustomer.trim()}>
                  Lägg till
                </button>
              </>
            )}
          </div>

          <div className="gw-actions">
            <button onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button
              onClick={nextStep}
              className="gw-btn gw-btn-primary"
              disabled={!form.customer?.trim()}
            >
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 3 */}
      {Number(currentStep) === 3 && (
        <div className="gw-card">
          {renderHeader()}
          <div className="mb-4">
            <input
              name="serial"
              type="text"
              placeholder="Serienummer"
              value={form.serial ?? ""}
              onChange={handleInputChange}
              className="gw-input relative z-10 flex-1 pr-10"
            />
          </div>
          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary" disabled={!stepValid}>
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 4 */}
      {Number(currentStep) === 4 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={form.manufacturer ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  userEditedManufacturerRef.current = true;
                  setShowNewManufacturerInput(true);
                  setForm((prev: any) => ({ ...prev, manufacturer: "" }));
                } else {
                  userEditedManufacturerRef.current = true;
                  setShowNewManufacturerInput(false);
                  setForm((prev: any) => ({ ...prev, manufacturer: value }));
                }
              }}
              className="gw-input"
            >
              <option value="">Tillverkare</option>
              {(Array.isArray(manufacturerList) ? manufacturerList : []).map((man: any) => (
                <option key={man} value={man}>
                  {man}
                </option>
              ))}

              {aiSuggestions.manufacturer &&
                !(Array.isArray(manufacturerList) ? manufacturerList : []).some(
                  (m: any) => toKey(m) === toKey(aiSuggestions.manufacturer!)
                ) && (
                  <option key="ai-manufacturer" value={aiSuggestions.manufacturer}>
                    {aiSuggestions.manufacturer} (AI-förslag)
                  </option>
                )}
              <option value="new">Lägg till ny tillverkare</option>
            </select>

            {showNewManufacturerInput && (
              <>
                <input
                  type="text"
                  placeholder="Ny tillverkare"
                  value={newManufacturer}
                  onChange={(e) => { userEditedManufacturerRef.current = true; setNewManufacturer(e.target.value); }}
                  className="gw-input"
                />
                {manufacturerExists && (
                  <div className="text-xs text-red-800">Tillverkaren finns redan.</div>
                )}
                <button
                  type="button"
                  onClick={handleNewManufacturerAdd}
                  disabled={!newManufacturer.trim() || manufacturerExists}
                  title={manufacturerExists ? "Dublett: kan inte spara" : "Lägg till"}
                  className="gw-btn gw-btn-secondary"
                >
                  Lägg till
                </button>
              </>
            )}
          </div>

          {aiSuggestions.manufacturer && (
            <div className="mt-1.5 text-xs text-blue-600">
              AI föreslog: <b>{aiSuggestions.manufacturer}</b>
            </div>
          )}

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary" disabled={!stepValid}>
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 5 */}
      {Number(currentStep) === 5 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={form.model ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "new") {
                  setShowNewModelInput(true);
                  setForm((prev: any) => ({ ...prev, model: "" }));
                } else {
                  setShowNewModelInput(false);
                  setForm((prev: any) => ({ ...prev, model: val }));
                }
              }}
              className="gw-input"
              disabled={!form.manufacturer}
            >
              <option value="">{form.manufacturer ? "Modell" : "Välj tillverkare först"}</option>

              {(Array.isArray(modelList) ? modelList : []).map((m: any) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}

              {aiSuggestions.model &&
                !(Array.isArray(modelList) ? modelList : []).some(
                  (m: any) => toKey(m) === toKey(aiSuggestions.model!)
                ) && (
                  <option key="ai-model" value={aiSuggestions.model}>
                    {aiSuggestions.model} (AI-förslag)
                  </option>
                )}

              {form.manufacturer && <option value="new">Lägg till ny modell</option>}
            </select>

            {showNewModelInput && (
              <>
                <input
                  type="text"
                  placeholder="Ny modell"
                  value={newModel}
                  onChange={(e) => { userEditedModelRef.current = true; setNewModel(e.target.value); }}
                  className="gw-input"
                />
                {modelExistsForThisManufacturer && (
                  <div className="text-xs text-red-800">
                    Modell finns redan för tillverkare.
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleNewModelAdd}
                  className="gw-btn gw-btn-secondary"
                  disabled={!newModel.trim() || modelExistsForThisManufacturer}
                  title={modelExistsForThisManufacturer ? "Dublett: kan inte spara" : "Lägg till"}
                >
                  Lägg till
                </button>
              </>
            )}
          </div>

          {aiSuggestions.model && (
            <div className="mt-1.5 text-xs text-blue-600">
              AI föreslog: <b>{aiSuggestions.model}</b>
            </div>
          )}

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary" disabled={!stepValid}>
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 6 */}
      {Number(currentStep) === 6 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={rpTypeOpts.some((t: any) => t.id === form.productTypeId)
                ? form.productTypeId
                : (form.productType || "")}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewProductTypeInput(true);
                  setForm((prev: any) => ({ ...prev, productType: "", productTypeId: "" }));
                } else {
                  setShowNewProductTypeInput(false);
                  setForm((prev: any) => ({
                    ...prev,
                    ...resolveProductTypeSelection(rpTypeOpts, value),
                  }));
                }
              }}
              className="gw-input"
            >
              <option value="">Produkttyp</option>

              {(Array.isArray(rpTypeOpts) ? rpTypeOpts : []).map((t: any) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}

              {aiSuggestions.productType &&
                !(Array.isArray(rpTypeOpts) ? rpTypeOpts : []).some(
                  (t: any) => toKey(t.label) === toKey(aiSuggestions.productType!)
                ) && (
                  <option key="ai-product-type" value={aiSuggestions.productType}>
                    {aiSuggestions.productType} (AI-förslag)
                  </option>
                )}

              <option value="new">Lägg till ny produkttyp</option>
            </select>

            {showNewProductTypeInput && (
              <ProductTypeCreateInline
                value={newProductType}
                onChange={setNewProductType}
                productTypeExists={productTypeExists}
                ensureProductTypeInDb={ensureProductTypeInDb}
                setRpTypeOpts={setRpTypeOpts}
                onCreated={(option: any) => {
                  setForm((p: any) => ({ ...p, productType: option.label, productTypeId: option.id }));
                  setShowNewProductTypeInput(false);
                  setNewProductType("");
                }}
                setRpTypesPrimed={setRpTypesPrimed}
                showExistsWarning
              />
            )}
          </div>

          {aiSuggestions.productType && (
            <div className="mt-1.5 text-xs text-blue-600">
              AI föreslog: <b>{aiSuggestions.productType}</b>
            </div>
          )}

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button
              type="button"
              onClick={nextStep}
              className="gw-btn gw-btn-primary"
              disabled={!form.productType?.trim()}
            >
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 7 */}
      {Number(currentStep) === 7 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="mb-4">
            <input
              name="warrantyStartDate"
              type="text"
              inputMode="numeric"
              placeholder="YYYY-MM-DD"
              value={form.warrantyStartDate ?? ""}
              onInput={handleIsoDateInput}
              onChange={() => { /* tom: vi använder onInput ovan för maskning + state */ }}
              onPaste={(e) => {
                const text = e.clipboardData?.getData("text") || "";
                const iso = parseLooseDateToISO(text);
                if (iso) { e.preventDefault(); setForm((p: any) => ({ ...p, warrantyStartDate: iso })); }
              }}
              className="gw-input"
            />
          </div>

          {(() => {
            const serialBase = normalizeSerialKey(splitSerialParts(form.serial || "").base);
            if (!serialBase) return null;

            return (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openWarranty({
                      manufacturer: form.manufacturer,
                      serialBase,
                      form,
                    })}
                    className="gw-btn gw-btn-primary border-2 font-bold"
                    title={`Öppna garantisida & kopiera ${serialBase}`}
                  >
                    Öppna garantisida & kopiera serienummer
                  </button>
                  <button type="button" className="gw-btn gw-btn-secondary" onClick={() => markNoWarranty(true)}>
                    Ingen garanti
                  </button>
                </div>
                <div className="mt-1.5 text-xs text-muted">
                  Serienumret kopieras automatiskt. Länken byggs via leverantörs-lookup (HP stöds nu)
                  och faller tillbaka till generell sida om exakt länk inte kan tas fram.
                </div>
              </div>
            );
          })()}

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary">
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 8: Adapter & skador */}
      {Number(currentStep) === 8 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                name="chargerIncluded"
                type="checkbox"
                checked={!!form.chargerIncluded}
                onChange={handleInputChange}
              />
              Adapter medföljer
            </label>
          </div>

          <div className="mb-4">
            <textarea
              name="damageNotes"
              placeholder="Eventuella skador"
              value={form.damageNotes ?? ""}
              onChange={handleInputChange}
              className="gw-input min-h-[88px]"
            />
          </div>

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary">Nästa</button>
          </div>
        </div>
      )}

      {/* STEG 9: Artikelnummer hos Convit */}
      {Number(currentStep) === 9 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={form.articleNumber ?? ""}
              disabled={!form.customer}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "new") {
                  setShowNewArticleInput(true);
                  setForm((p: any) => ({ ...p, articleNumber: "" }));
                } else {
                  setShowNewArticleInput(false);
                  setForm((p: any) => ({ ...p, articleNumber: v }));
                }
              }}
              className="gw-input w-full"
            >
              <option value="">{form.customer ? "Artikelnummer hos Convit" : "Välj kund först"}</option>
              {(Array.isArray(articleList) ? articleList : []).map((a: any) => (
                <option key={a} value={a}>{a}</option>
              ))}
              {form.customer && <option value="new">Lägg till ny artikel</option>}
            </select>

            {showNewArticleInput && (
              <>
                <input
                  type="text"
                  placeholder="Ny artikel"
                  value={newArticle}
                  onChange={(e) => setNewArticle(e.target.value)}
                  className="gw-input"
                />
                <button type="button" onClick={handleNewArticleAdd} className="gw-btn gw-btn-secondary" disabled={!newArticle.trim()}>
                  Lägg till
                </button>
              </>
            )}
          </div>

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button
              type="button"
              onClick={nextStep}
              className="gw-btn gw-btn-primary"
              disabled={!stepValid}
            >
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* STEG 10: Bilder */}
      {Number(currentStep) === 10 && (
        <div className="gw-card">
          {renderHeader()}

          <div className="gw-photo-grid grid grid-cols-4 gap-2">
            {([ "keyboard", "screen", "underside", "topside" ] as PhotoKey[]).map((type) => {
              const src = (thumbnailPreviews ?? {})[type];
              const label =
                type === "keyboard" ? "Tangentbord" :
                  type === "screen" ? "Skärm" :
                    type === "underside" ? "Undersida" : "Ovansida";

              return (
                <div key={type} className="gw-photo-card">
                  <input
                    id={`photo-${type}`}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handlePhotoChange(e, type)}
                  />

                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => document.getElementById(`photo-${type}`)?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        document.getElementById(`photo-${type}`)?.click();
                      }
                    }}
                  >
                    {src ? (
                      <img className="gw-photo-img" src={src} alt={`${label} preview`} />
                    ) : (
                      <div className="gw-photo-placeholder">
                        Klicka för att lägga till<br />{label.toLowerCase()}
                      </div>
                    )}
                  </div>

                  {src && (
                    <button
                      type="button"
                      className="gw-photo-remove"
                      onClick={() => handleRemovePhoto(type)}
                      title={`Ta bort ${label}`}
                      aria-label={`Ta bort ${label}`}
                    >
                      ×
                    </button>
                  )}

                  <div className="gw-photo-title">{label}</div>
                </div>
              );
            })}
          </div>

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={nextStep} className="gw-btn gw-btn-primary">Nästa</button>
          </div>
        </div>
      )}

      {/* STEG 11 */}
      {Number(currentStep) === 11 && (
        <div className="gw-card relative">
          {renderHeader()}
          {isSaving && (
            <div className="gw-progress-overlay" aria-live="assertive">
              <div className="gw-progress-track">
                <div className="gw-progress-fill" style={{ width: `${overallProgress}%` }} />
              </div>
              <div className="text-sm text-text">
                Sparar… {overallProgress}%
              </div>
            </div>
          )}

          <h3 className="gw-h3">Sammanfattning</h3>

          <div className="grid grid-cols-2 gap-x-8 leading-relaxed">
            <div><b>Ordernr:</b> {form.orderNumber || "—"}</div>
            <div><b>Tillverkare:</b> {form.manufacturer || "—"}</div>
            <div><b>Modell:</b> {form.model || "—"}</div>
            <div><b>Serienummer:</b> {form.serial || "—"}</div>
            <div><b>Warranty start:</b> {form.warrantyStartDate || "—"}</div>
            <div><b>Adapter medföljer:</b> {form.chargerIncluded ? "Ja" : "Nej"}</div>
            <div><b>Skador:</b> {form.damageNotes || "—"}</div>
            <div><b>Kund:</b> {form.customer || "—"}</div>
            <div><b>Artikelnummer:</b> {form.articleNumber || "—"}</div>
            <div className="mt-2">
              <b>Bilder:</b>{" "}
              {Object.values(thumbnailPreviews ?? {}).some(Boolean) ? "Valda bilder" : "Inga bilder valda"}
            </div>
          </div>

          <div className="gw-actions">
            <button type="button" onClick={prevStep} className="gw-btn gw-btn-secondary">Tillbaka</button>
            <button type="button" onClick={handleSave} className="gw-btn gw-btn-primary" disabled={isSaving}>
              {isSaving ? "Sparar…" : "Spara enhet"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
